import { ReactNode, useCallback, useMemo, useState } from 'react'
import ConfettiExplosion from 'react-confetti-explosion'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { theseus } from '../../../../api/theseus'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { selectUser } from '../../../../modules/user/selectors'
import { Checkbox } from '../../../../page_components/DropDetails/ChapterView'
import TaskBody from '../../TaskBody'
import { SubTask } from '../../../../page_components/PoolDetails/Missions/types'
import routes from '../../../../router/routes'
import AnimatedLoader from '../../../AnimatedLoader'
import { ThemedButton } from '../../../ThemedButton'
import { Box } from '../../../UI/Box'

export type Props = {
  subtask: SubTask
  taskId: number
  updateTaskSubmissions: () => Promise<unknown>
  taskAmount: ReactNode
}

const EmailTask = ({
  subtask,
  taskId,
  updateTaskSubmissions,
  taskAmount,
}: Props) => {
  const { id: subTaskId, publicName, instructions } = subtask
  const title = publicName ?? 'Subscribe to our mailing list'
  const description =
    instructions ?? 'Simply press the subscribe button below to get our updates'
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  const project = useSelector(selectCurrentProject)
  const projectId = project?.id
  const user = useSelector(selectUser)
  const userHasEmail = !!user?.emailAddress
  const userHasVerifiedEmail = !!user?.hasVerifiedEmail

  const location = useLocation()
  const history = useHistory()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  const updateSearchParam = useCallback(
    (paramName: string, value: string) => {
      searchParams.set(paramName, value)
      history.push(`${location.pathname}?${searchParams.toString()}`)
    },
    [history, location.pathname, searchParams],
  )

  const selfDestruct = useCallback(
    (ms = 2000) => {
      setTimeout(() => {
        updateSearchParam('active_mission', '')
      }, ms)
    },
    [updateSearchParam],
  )

  const userIsVerified = userHasVerifiedEmail && userHasEmail
  const submit = useCallback(async () => {
    try {
      if (!userIsVerified) {
        history.push(routes.account)
        return
      }

      setError(null)
      setIsLoading(true)
      const { status } = await theseus.post(
        '/task-submission/b2c/email-task-submission',
        {
          subTaskId,
          projectId,
          taskId,
        },
      )
      if (status !== 200) throw new Error('Something went wrong')

      await updateTaskSubmissions()
      selfDestruct()
      setIsComplete(true)
    } catch (error) {
      console.error(error)
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }, [
    userIsVerified,
    subTaskId,
    projectId,
    taskId,
    updateTaskSubmissions,
    selfDestruct,
    history,
  ])

  return (
    <TaskBody
      taskId={taskId}
      title={title}
      description={
        userIsVerified
          ? description
          : 'You need to verify your email address first'
      }
      error={error}
      extra={taskAmount}
      actions={
        <ActionBlock
          userIsVerified={userIsVerified}
          isLoading={isLoading}
          onClick={submit}
          isComplete={isComplete}
        />
      }
    />
  )
}

export default EmailTask

const CheckContainer = styled(Box)<{ isVisible: boolean }>`
  max-width: 0px;
  overflow: hidden;
  opacity: 0;
  transition: 0.3s ease-in-out;
  ${({ isVisible }) =>
    isVisible &&
    css`
      opacity: 1;
      max-width: 28px;
      padding-right: 8px;
    `}
`

function ActionBlock({
  userIsVerified,
  isLoading,
  onClick,
  isComplete,
}: {
  userIsVerified: boolean
  isLoading: boolean
  onClick: () => void
  isComplete: boolean
}) {
  return (
    <ThemedButton
      onClick={onClick}
      disabled={isLoading || isComplete}
      $styleType="primary"
    >
      {isComplete && <ConfettiExplosion zIndex={10000} />}
      <CheckContainer isVisible={isComplete}>
        <Checkbox />
      </CheckContainer>
      {isLoading ? (
        <AnimatedLoader size={14} />
      ) : isComplete ? (
        'Subscribed'
      ) : !userIsVerified ? (
        'Verify email'
      ) : (
        'Subscribe'
      )}
    </ThemedButton>
  )
}
